import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-select';
import { PartExclusionDetail } from '../../models/part-exclusion.model';
import { PartExclusionService } from '../../services/part-exclusion.service';
import { ToastrService } from '../../services/toastr-service';

@Component({
  selector: 'app-part-exclusion-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './part-exclusion-form.component.html',
  styleUrl: './part-exclusion-form.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PartExclusionFormComponent implements OnInit {
  readonly form = this.formBuilder.group({
    repairMode: ['', Validators.required],
    partNumber: ['', Validators.required],
    description: [''],
    exclusivePartNumbers: ['', Validators.required],
    exclusivePartNumberInput: [''],
  });
  readonly editMode = signal(false);
  readonly deleting = signal(false);
  readonly submitting = signal(false);
  readonly exclusivePartNumbers = signal<string[]>([]);
  modes: string[] = [];
  detail: PartExclusionDetail | null = null;
  private repairMode = '';
  private partNumber = '';

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly service: PartExclusionService,
    private readonly toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.service.getAllModes().subscribe((modes) => (this.modes = modes));
    this.repairMode = this.route.snapshot.paramMap.get('repairMode') ?? '';
    this.partNumber = this.route.snapshot.paramMap.get('partNumber') ?? '';
    if (!this.repairMode || !this.partNumber) return;
    this.editMode.set(true);
    this.service.getByKey(this.repairMode, this.partNumber).subscribe((detail) => {
      if (!detail) {
        this.toastr.showToastr('Part exclusion rule not found', 'error');
        this.router.navigate(['/master-data/part-exclusion']);
        return;
      }
      this.detail = detail;
      this.form.patchValue({
        repairMode: detail.repairMode,
        partNumber: detail.partNumber,
        description: detail.description || '',
      });
      this.exclusivePartNumbers.set(this.parseExclusivePartNumbers(detail.exclusionPartNumber));
      this.syncExclusivePartNumbers();
      this.form.controls.repairMode.disable();
      this.form.controls.partNumber.disable();
    });
  }

  onModeSelected(event: Event): void {
    const customEvent = event as CustomEvent;
    const selectedMode = customEvent?.detail?.value ?? customEvent?.detail;
    if (selectedMode) {
      this.form.controls.repairMode.setValue(selectedMode);
    }
  }

  onPartNumberChange(_event?: Event): void {
    const partControl = this.form.controls.partNumber;
    const selectedPart = String(partControl.value ?? '').trim().toUpperCase();
    if (!selectedPart) {
      this.form.controls.description.setValue('');
      return;
    }
    partControl.setValue(selectedPart);
    this.form.controls.description.setValue('');
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    const value = this.form.getRawValue();
    const request = { repairMode: value.repairMode!, partNumber: value.partNumber!, exclusivePartNumbers: value.exclusivePartNumbers! };
    const operation = this.editMode()
      ? this.service.update(this.repairMode, this.partNumber, { exclusivePartNumbers: request.exclusivePartNumbers })
      : this.service.create(request);
    operation.subscribe({
      next: () => {
        this.toastr.showToastr(`Part exclusion rule ${this.editMode() ? 'updated' : 'added'} successfully`, 'success');
        this.router.navigate(['/master-data/part-exclusion', request.repairMode, request.partNumber]);
      },
      error: (error: HttpErrorResponse) => {
        this.submitting.set(false);
        this.toastr.showToastr(error.error?.message ?? 'Failed to save part exclusion rule', 'error');
      },
    });
  }

  delete(): void {
    this.deleting.set(false);
    this.submitting.set(true);
    this.service.delete(this.repairMode, this.partNumber).subscribe({
      next: () => {
        this.toastr.showToastr('Part exclusion rule deleted successfully', 'success');
        this.router.navigate(['/master-data/part-exclusion']);
      },
      error: () => {
        this.submitting.set(false);
        this.toastr.showToastr('Failed to delete part exclusion rule', 'error');
      },
    });
  }

  cancel(): void {
    this.router.navigate(['/master-data/part-exclusion']);
  }

  addExclusivePartNumber(): void {
    const input = this.form.controls.exclusivePartNumberInput;
    const partNumber = input.value?.trim() ?? '';
    if (!partNumber) return;

    if (this.exclusivePartNumbers().includes(partNumber)) {
      this.toastr.showToastr('Part number already added', 'warning');
      return;
    }

    this.exclusivePartNumbers.update((partNumbers) => [...partNumbers, partNumber]);
    this.syncExclusivePartNumbers();
    input.setValue('');
    this.form.markAsDirty();
  }

  removeExclusivePartNumber(partNumber: string): void {
    this.exclusivePartNumbers.update((partNumbers) => partNumbers.filter((value) => value !== partNumber));
    this.syncExclusivePartNumbers();
    this.form.markAsDirty();
  }

  private syncExclusivePartNumbers(): void {
    this.form.controls.exclusivePartNumbers.setValue(this.exclusivePartNumbers().join(','));
    this.form.controls.exclusivePartNumbers.markAsTouched();
    this.form.controls.exclusivePartNumbers.updateValueAndValidity();
  }

  private parseExclusivePartNumbers(value: string): string[] {
    return value
      .split(',')
      .map((partNumber) => partNumber.trim())
      .filter(Boolean);
  }
}
